import { useState, type DragEvent, type KeyboardEvent } from 'react';
import type { PublicNoteFragment, PublicRetroNote } from '@planning-session/shared';
import { CloseIcon, UndoIcon } from '../ui/Icon.js';
import styles from './NoteCard.module.css';

export interface NoteCardProps {
  readonly note: PublicRetroNote;
  readonly phase: 'writing' | 'revealed';
  readonly concealing: boolean;
  /**
   * 0 until the board reveals, then 1 and steady — reveal is one-way for a
   * retro's lifetime, so this only ever changes once. Kept as a count rather
   * than a boolean because a boolean derived from "did the transition just
   * happen" flips true then false across two renders, which is two key
   * changes; this settles in one.
   */
  readonly revealSeq: number;
  /** This note's place in the board-wide reveal wave — see Column's stagger. */
  readonly revealDelayMs: number;
  readonly canVote: boolean;
  readonly canMerge: boolean;
  readonly isAuthor: boolean;
  readonly canRemove: boolean;
  /** Unmerging is a host power, same as merging — see combineNotes/unmergeNote. */
  readonly canUnmerge: boolean;
  readonly draggable: boolean;
  onEdit?: (text: string) => void;
  onRemove?: () => void;
  onUnmerge?: () => void;
  onVote?: () => void;
  onUnvote?: () => void;
  onDragStart?: () => void;
  onDragOver?: (event: DragEvent) => void;
  onDrop?: () => void;
}

/**
 * A note, or an empty placeholder seat — same estimation-card semantics as the
 * planning table: a dashed outline marks the absence, a filled surface marks
 * content. Your own note carries a focus-tinted border and fill so it reads as
 * "mine" rather than "disabled."
 */
// Matches --duration-fast in tokens.css. Kept as a literal because the exit
// must finish before the real onRemove fires, and JS can't read a CSS custom
// property's value without a DOM round-trip that isn't worth it here.
const EXIT_DURATION_MS = 120;

export function NoteCard({
  note,
  phase,
  concealing,
  revealSeq,
  revealDelayMs,
  canVote,
  canMerge,
  isAuthor,
  canRemove,
  canUnmerge,
  draggable,
  onEdit,
  onRemove,
  onUnmerge,
  onVote,
  onUnvote,
  onDragStart,
  onDragOver,
  onDrop,
}: NoteCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.fragments?.[0]?.text ?? '');
  const [removing, setRemoving] = useState(false);

  if (note.fragments === null) {
    return <div className={styles.placeholder} aria-hidden="true" />;
  }

  // A merged note can only be split apart via unmerge (see domain.ts's `note_is_merged`
  // guard) — free-text editing would have no single author left to attribute the result to.
  const merged = note.fragments.length > 1;
  const only = note.fragments[0]!;

  function handleRemove() {
    setRemoving(true);
    window.setTimeout(() => onRemove?.(), EXIT_DURATION_MS);
  }

  function commitEdit() {
    const trimmed = draft.trim();
    if (trimmed.length > 0 && trimmed !== only.text) onEdit?.(trimmed);
    setEditing(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Escape') {
      setDraft(only.text);
      setEditing(false);
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      commitEdit();
    }
  }

  const canEdit = !merged && isAuthor && phase === 'writing';

  function handleTextKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setEditing(true);
    }
  }

  // Before reveal in an anonymous retro, every other author is concealed, so your own real
  // name stands out as the one line not reading "Anonymous" — "You" carries the same
  // ownership signal the tinted border gives sighted users, in text a screen reader
  // announces too. Merging only ever happens post-reveal, so a merged note's fragments are
  // never concealed and always carry their real authors.
  function fragmentAuthorLabel(fragment: PublicNoteFragment): string {
    return fragment.mine && concealing ? 'You' : fragment.authorName ?? 'Anonymous';
  }

  const soleAuthorLabel = merged ? null : fragmentAuthorLabel(only);

  return (
    <div
      className={`${styles.note} ${note.mine ? styles.mine : ''} ${removing ? styles.removing : ''}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {editing ? (
        <textarea
          className={styles.editArea}
          value={draft}
          autoFocus
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <div
          className={canEdit ? styles.editable : undefined}
          role={canEdit ? 'button' : undefined}
          tabIndex={canEdit ? 0 : undefined}
          aria-label={canEdit ? 'Edit note' : undefined}
          onClick={() => canEdit && setEditing(true)}
          onKeyDown={canEdit ? handleTextKeyDown : undefined}
        >
          {note.fragments.map((fragment, index) => {
            const authorLabel = fragmentAuthorLabel(fragment);
            return (
              <div key={index} className={styles.fragment}>
                <p className={styles.line}>{fragment.text}</p>
                {merged && (
                  <span
                    key={`${authorLabel}-${revealSeq}`}
                    className={styles.fragmentAuthor}
                    style={phase === 'revealed' ? { animationDelay: `${revealDelayMs}ms` } : undefined}
                  >
                    {authorLabel}
                  </span>
                )}
              </div>
            );
          })}
          {canEdit && <span className={styles.editHint}>Click to edit</span>}
        </div>
      )}

      <div className={styles.footer}>
        {/*
         * Keyed by the label AND the reveal count: in anonymous rooms the
         * label itself changes at reveal (Anonymous/You -> the real name),
         * which already remounts this span. In visible rooms the name was
         * never concealed, so the label never changes — `revealSeq` is what
         * forces the one-time remount there instead. Arriving on an
         * already-revealed board (a late joiner, a refresh) mounts straight
         * into the revealed key, so the wave plays once on arrival too — the
         * same entrance every note already gets, just with the name included.
         * A merged note shows its authors per-fragment above instead, so the
         * footer carries no author of its own.
         */}
        {soleAuthorLabel !== null && (
          <span
            key={`${soleAuthorLabel}-${revealSeq}`}
            className={styles.author}
            style={phase === 'revealed' ? { animationDelay: `${revealDelayMs}ms` } : undefined}
          >
            {soleAuthorLabel}
          </span>
        )}

        <div className={styles.actions}>
          {canUnmerge && onUnmerge && (
            <button type="button" className={styles.iconButton} aria-label="Unmerge note" title="Split the last-merged note back off" onClick={onUnmerge}>
              <UndoIcon size={14} />
            </button>
          )}
          {canRemove && onRemove && (
            <button type="button" className={styles.iconButton} aria-label="Remove note" onClick={handleRemove} disabled={removing}>
              <CloseIcon size={14} />
            </button>
          )}
          {canVote && (
            <button
              type="button"
              className={`${styles.vote} ${note.votedByMe ? styles.voted : ''}`}
              onClick={note.votedByMe ? onUnvote : onVote}
              aria-pressed={note.votedByMe}
            >
              <span key={note.voteCount} className={styles.voteCount}>
                {note.voteCount ?? 0}
              </span>
            </button>
          )}
        </div>
      </div>

      {canMerge && <div className={styles.mergeHint}>Drag onto another note to combine</div>}
    </div>
  );
}
